import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ModuleSetting {
  id: string;
  tenant_id: string | null;
  module_key: string;
  is_enabled: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export const MODULE_DEFINITIONS = [
  { key: 'calendar', label: 'Agenda', description: 'Outlook agenda integratie', icon: 'Calendar' },
  { key: 'leads', label: 'Leads', description: 'Lead management', icon: 'Users' },
  { key: 'organisations', label: 'Organisaties', description: 'Organisatie beheer', icon: 'Building2' },
  { key: 'contacts', label: 'Contacten', description: 'Contact beheer', icon: 'Users' },
  { key: 'projects', label: 'Projecten', description: 'Project management', icon: 'FolderKanban' },
  { key: 'quotes', label: 'Offertes', description: 'Offerte beheer', icon: 'FileText' },
  { key: 'businesscases', label: 'Businesscases', description: 'Businesscase calculator', icon: 'Calculator' },
  { key: 'flexe', label: 'Flex-E Simulatie', description: 'Energie simulatie tool', icon: 'Activity' },
  { key: 'charging', label: 'Charging', description: 'Laadinfrastructuur beheer', icon: 'Zap' },
  { key: 'sites', label: 'Locaties', description: 'Locatie beheer', icon: 'MapPin' },
  { key: 'battery_planner', label: 'Battery Planner', description: 'Batterij planning', icon: 'Battery' },
  { key: 'todos', label: "To-do's", description: 'Taken beheer', icon: 'CheckSquare' },
  { key: 'notifications', label: 'Notificaties', description: 'Meldingen', icon: 'Bell' },
  { key: 'website', label: 'Website', description: 'Website content beheer', icon: 'Globe' },
];

export const useModuleSettings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['module-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('module_settings')
        .select('*')
        .order('display_order');

      if (error) {
        console.error('Error fetching module settings:', error);
        throw error;
      }
      return data as ModuleSetting[];
    },
  });

  const isModuleEnabled = (moduleKey: string): boolean => {
    const setting = settings.find(s => s.module_key === moduleKey);
    // Default to enabled if no setting found
    return setting?.is_enabled ?? true;
  };

  const toggleModuleMutation = useMutation({
    mutationFn: async ({ moduleKey, isEnabled }: { moduleKey: string; isEnabled: boolean }) => {
      // Check if setting exists
      const existing = settings.find(s => s.module_key === moduleKey);

      if (existing) {
        const { error } = await supabase
          .from('module_settings')
          .update({ is_enabled: isEnabled, updated_at: new Date().toISOString() })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Create new setting
        const { error } = await supabase
          .from('module_settings')
          .insert({
            module_key: moduleKey,
            is_enabled: isEnabled,
            tenant_id: null, // Global setting
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['module-settings'] });
      toast({
        title: 'Module instellingen bijgewerkt',
        description: 'De wijziging is opgeslagen.',
      });
    },
    onError: (error: any) => {
      console.error('Error toggling module:', error);
      toast({
        title: 'Fout bij opslaan',
        description: 'Kon de module instelling niet bijwerken.',
        variant: 'destructive',
      });
    },
  });

  const toggleModule = (moduleKey: string, isEnabled: boolean) => {
    toggleModuleMutation.mutate({ moduleKey, isEnabled });
  };

  return {
    settings,
    isLoading,
    isModuleEnabled,
    toggleModule,
    isUpdating: toggleModuleMutation.isPending,
  };
};
